import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { ResponseError } from 'superagent';

import { useErrorSnackbarWithRetry } from 'components/Snackbars/useErrorSnackbarWithRetry';
import { getHttpStatusFromError, isAuthenticationError } from 'utils/httpError';

export interface MutationOptions<TData = unknown, TVariables = void, TContext = unknown>
  extends UseMutationOptions<TData, ResponseError, TVariables, TContext> {
  mutationDescription: string;
  errorMessageGetter?: (error: ResponseError) => string;
  showRetryButton?: boolean;
}

export const useMutationWithErrorSnackbar = <TData = unknown, TVariables = void, TContext = unknown>({
  showRetryButton,
  mutationDescription,
  errorMessageGetter = (error: ResponseError) => error?.response?.body?.message || error?.message,
  ...options
}: MutationOptions<TData, TVariables, TContext>): ReturnType<
  typeof useMutation<TData, ResponseError, TVariables, TContext>
> => {
  const { enqueueErrorSnackbar } = useErrorSnackbarWithRetry({ errorMessageGetter });

  const { mutate, mutateAsync, ...returnValue } = useMutation<TData, ResponseError, TVariables, TContext>({
    ...options,
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
      if ((error as any)?.code === 'ABORTED' || (error as any)?.name === 'AbortError') {
        return;
      }
      const message = errorMessageGetter(error);
      const status = getHttpStatusFromError(error);
      if (status === 500 || status === undefined) {
        console.error('API error', error, message);
      }
      if (isAuthenticationError(error)) {
        console.warn('user unauthorized');
      }
      enqueueErrorSnackbar(error, mutationDescription, showRetryButton ? () => mutate(variables) : undefined);
    },
  });

  return { ...returnValue, mutate, mutateAsync };
};
